import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { theme } from '../../styles/theme';
import { Container } from '../common/Container';
import { Button } from '../common/Button';

const Section = styled.section`
  padding: ${theme.spacing['4xl']} 0;
  background: linear-gradient(180deg, 
    ${theme.colors.background} 0%, 
    ${theme.colors.backgroundSecondary} 100%
  );
`;

const Content = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${theme.spacing['3xl']};
  align-items: center;

  @media (min-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr 1fr;
  }
`;

const TextContent = styled.div`
  order: 2;

  @media (min-width: ${theme.breakpoints.lg}) {
    order: 1;
  }
`;

const Title = styled(motion.h2)`
  font-size: ${theme.fontSizes['2xl']};
  margin-bottom: ${theme.spacing.lg};
  
  @media (min-width: ${theme.breakpoints.md}) {
    font-size: ${theme.fontSizes['4xl']};
  }

  .highlight {
    background: linear-gradient(120deg, ${theme.colors.primaryLight} 0%, ${theme.colors.primary} 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const Description = styled(motion.p)`
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.text.secondary};
  line-height: 1.8;
  margin-bottom: ${theme.spacing.xl};
`;

const Features = styled(motion.ul)`
  list-style: none;
  padding: 0;
  margin: 0 0 ${theme.spacing.xl} 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: ${theme.spacing.md};
  font-size: ${theme.fontSizes.base};
  color: ${theme.colors.text.primary};

  &::before {
    content: '🦖';
    margin-right: ${theme.spacing.sm};
    font-size: ${theme.fontSizes.xl};
  }
`;

const DemoCard = styled(motion.div)`
  order: 1;
  background: white;
  border-radius: ${theme.radii.xl};
  padding: ${theme.spacing['2xl']};
  box-shadow: ${theme.shadows.xl};
  position: relative;
  overflow: hidden;

  @media (min-width: ${theme.breakpoints.lg}) {
    order: 2;
  }
`;

const DemoTitle = styled.h3`
  font-size: ${theme.fontSizes.lg};
  margin-bottom: ${theme.spacing.lg};
  color: ${theme.colors.text.primary};
`;

const TemplateExample = styled.div`
  background: ${theme.colors.backgroundSecondary};
  border-radius: ${theme.radii.lg};
  padding: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};
  font-family: ${theme.fonts.code};
  font-size: ${theme.fontSizes.sm};
  line-height: 1.6;
`;

const Blank = styled.span`
  background: linear-gradient(135deg, ${theme.colors.primaryLight}, ${theme.colors.primary});
  color: white;
  padding: 2px 8px;
  border-radius: ${theme.radii.sm};
  margin: 0 4px;
  font-weight: 600;
  white-space: nowrap;
`;

const Badge = styled.div`
  position: absolute;
  top: -10px;
  right: 20px;
  background: linear-gradient(135deg, #ff6b6b, #ff9f43);
  color: white;
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  border-radius: ${theme.radii.full};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  transform: rotate(3deg);
`;

const CTAButton = styled(Button)`
  margin-top: ${theme.spacing.lg};
`;

export const FossilFillsFeature: React.FC = () => {
  return (
    <Section>
      <Container>
        <Content>
          <TextContent>
            <Title
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              Transform Prompts with <span className="highlight">Fossil Fills</span>
            </Title>
            <Description
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Our Mad Libs-style template system makes it easy to create 
              powerful, domain-specific prompts. Fill in the blanks with 
              dropdowns, and watch your prompts evolve!
            </Description>
            <Features
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <FeatureItem>
                <div>
                  <strong>Domain-Specific Templates</strong>
                  <br />
                  Specialized templates for screenwriting, programming, marketing, and more
                </div>
              </FeatureItem>
              <FeatureItem>
                <div>
                  <strong>Interactive Dropdowns</strong>
                  <br />
                  Smart suggestions for each blank based on your domain
                </div>
              </FeatureItem>
              <FeatureItem>
                <div>
                  <strong>Educational Insights</strong>
                  <br />
                  Learn why each element makes your prompts more effective
                </div>
              </FeatureItem>
              <FeatureItem>
                <div>
                  <strong>Template Editor</strong>
                  <br />
                  Domain experts can customize and improve templates
                </div>
              </FeatureItem>
            </Features>
            <CTAButton as={Link} to="/fossil_fills" size="large">
              Try Fossil Fills Now →
            </CTAButton>
          </TextContent>

          <DemoCard
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Badge>NEW!</Badge>
            <DemoTitle>Example: Screenwriting Template</DemoTitle>
            <TemplateExample>
              You are an <Blank>experienced screenwriter</Blank> specializing in{' '}
              <Blank>dramatic scenes</Blank>. Create a <Blank>compelling scene</Blank>{' '}
              between <Blank>complex characters</Blank> that{' '}
              <Blank>drives the narrative forward</Blank> with{' '}
              <Blank>high emotional tension</Blank> and{' '}
              <Blank>authentic dialogue</Blank>.
            </TemplateExample>
            <p style={{ 
              fontSize: theme.fontSizes.sm, 
              color: theme.colors.text.secondary,
              marginTop: theme.spacing.md 
            }}>
              Each highlighted field becomes an interactive dropdown with 
              domain-specific suggestions!
            </p>
          </DemoCard>
        </Content>
      </Container>
    </Section>
  );
};